import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Package, TrendingUp, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductionDashboard = ({ isGrowerView = false }) => {
  const [roomCycles, setRoomCycles] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [harvestRooms, setHarvestRooms] = useState([]);
  const [todayEstimates, setTodayEstimates] = useState([]);
  const [showHarvestModal, setShowHarvestModal] = useState(null);
  const [showEstimateModal, setShowEstimateModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [cyclesRes, roomsRes, supervisorRes, estimatesRes] = await Promise.all([
        axios.get(`${API}/room-cycles?status=active`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/rooms`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/supervisor/today-work`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/production-estimates/today`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { estimates: [] } }))
      ]);

      // Handle both array and object with 'cycles' key response formats
      const cyclesData = Array.isArray(cyclesRes.data) ? cyclesRes.data : (cyclesRes.data.cycles || []);
      setRoomCycles(cyclesData);
      setRooms(roomsRes.data);
      setTodayEstimates(estimatesRes.data.estimates || []);

      // Get harvest rooms from supervisor/today-work production_rooms
      const harvestData = supervisorRes.data.production_rooms || [];
      setHarvestRooms(harvestData);
    } catch (error) {
      toast.error('Failed to fetch production data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading production data...</div>;
  }

  // Get room name from room ID
  const getRoomName = (roomId) => {
    const room = rooms.find(r => r.id === roomId);
    return room?.name || 'Unknown Room';
  };

  return (
    <div className="space-y-6">
      {/* Today's Estimates Summary */}
      {todayEstimates.length > 0 && (
        <Card className="border-blue-200 bg-blue-50/50" data-testid="today-estimates-summary">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-blue-900">
              <TrendingUp className="w-5 h-5" />
              Today's Production Estimates
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {todayEstimates.map((estimate, idx) => (
                <div key={estimate.id || `${estimate.room_id}-${estimate.is_morning ? 'AM' : 'PM'}-${idx}`} className="p-3 bg-white rounded-lg border border-blue-200">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">{estimate.room_name || getRoomName(estimate.room_id)}</p>
                      <p className="text-sm text-gray-500">{estimate.batch_name}</p>
                    </div>
                    <Badge className={estimate.is_morning ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}>
                      {estimate.is_morning ? 'AM' : 'PM'}
                    </Badge>
                  </div>
                  <div className="mt-2">
                    <p className="text-2xl font-bold text-gray-900">{estimate.estimate_kg} kg</p>
                    {estimate.notes && (
                      <p className="text-xs text-gray-500 mt-1 truncate">{estimate.notes}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 pt-3 border-t border-blue-200">
              <p className="text-sm text-blue-900">
                <strong>Total Estimated:</strong>{' '}
                {todayEstimates.reduce((sum, e) => sum + (e.estimate_kg || 0), 0).toFixed(1)} kg
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Today's Harvest Rooms */}
      <Card data-testid="harvest-rooms-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Package className="w-5 h-5 text-yellow-600" />
            Today's Harvest ({harvestRooms.length} rooms)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {harvestRooms.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No rooms in harvest phase today</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {harvestRooms.map((room, idx) => {
                const existingEstimate = todayEstimates.find(e => e.cycle_id === room.room_cycle_id);
                const isLastDay = room.is_last_day || room.current_day === 36 || room.current_day === 44;
                const isFlush1 = room.flush === 'Flush 1';
                
                return (
                  <div
                    key={room.room_cycle_id || `${room.room_name}-${idx}`}
                    className={`p-4 rounded-lg border-2 ${
                      isFlush1 ? 'border-yellow-300 bg-yellow-50' : 'border-orange-300 bg-orange-50'
                    }`}
                    data-testid={`harvest-room-${idx}`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <p className="text-lg font-bold text-gray-900">{room.room_name}</p>
                        <p className="text-sm text-gray-600">Batch: {room.batch_name}</p>
                        <p className="text-xs text-gray-500">Day {room.current_day}/45 ({room.flush} Day {room.day_in_flush})</p>
                      </div>
                      <Badge className={isFlush1 ? 'bg-yellow-200 text-yellow-800' : 'bg-orange-200 text-orange-800'}>
                        {room.flush}
                      </Badge>
                    </div>

                    {isLastDay && (
                      <p className="text-xs text-red-600 font-medium mb-2">Last day of flush!</p>
                    )}

                    {existingEstimate && (
                      <div className="mb-3 p-2 bg-white rounded border">
                        <p className="text-xs text-gray-500">Today's Estimate</p>
                        <p className="font-bold text-gray-900">{existingEstimate.estimated_weight_kg} kg</p>
                      </div>
                    )}

                    <div className="flex gap-2">
                      {isGrowerView && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                          onClick={() => setShowEstimateModal(room)}
                          data-testid={`estimate-btn-${idx}`}
                        >
                          <TrendingUp className="w-4 h-4 mr-1" />
                          {existingEstimate ? 'Update Est.' : 'Estimate'}
                        </Button>
                      )}
                      <Button
                        size="sm"
                        className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
                        onClick={() => setShowHarvestModal(room)}
                        data-testid={`log-harvest-btn-${idx}`}
                      >
                        Log Harvest
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Active Room Cycles Overview */}
      <Card data-testid="active-cycles-card">
        <CardHeader>
          <CardTitle>Active Room Cycles ({roomCycles.filter(c => c.status === 'active').length})</CardTitle>
        </CardHeader>
        <CardContent>
          {roomCycles.filter(c => c.status === 'active').length === 0 ? (
            <p className="text-gray-500 text-center py-8">No active room cycles</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
              {roomCycles.filter(c => c.status === 'active').map((cycle) => {
                const room = rooms.find(r => r.id === cycle.room_id);
                return (
                  <div
                    key={cycle.id}
                    className="p-3 rounded-lg border bg-white text-center"
                    data-testid={`cycle-${cycle.id}`}
                  >
                    <p className="font-medium text-sm">{room?.name || 'Unknown'}</p>
                    <p className="text-xs text-gray-500">Day {cycle.current_day}</p>
                    <Badge variant="outline" className="mt-1 text-xs">
                      {cycle.current_phase?.replace('_', ' ')}
                    </Badge>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Harvest Log Modal */}
      {showHarvestModal && (
        <HarvestLogModal
          room={showHarvestModal}
          onClose={() => setShowHarvestModal(null)}
          onSuccess={() => {
            setShowHarvestModal(null);
            fetchData();
          }}
        />
      )}

      {/* Production Estimate Modal */}
      {showEstimateModal && (
        <ProductionEstimateModal
          room={showEstimateModal}
          existingEstimate={getExistingEstimate(showEstimateModal.room_cycle_id)}
          onClose={() => setShowEstimateModal(null)}
          onSuccess={() => {
            setShowEstimateModal(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
};

// Harvest Log Modal
const HarvestLogModal = ({ room, onClose, onSuccess }) => {
  const [weight, setWeight] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!weight) {
      toast.error('Please enter harvest weight');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // Log production to /api/production
      await axios.post(`${API}/production`, {
        room_id: room.room_id,
        weight_kg: parseFloat(weight),
        quality_grade: 'A',
        notes: notes || `${room.flush} - Day ${room.day_in_flush}`
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      // Also mark the task as complete
      await axios.post(`${API}/room-cycles/complete-task`, {
        room_cycle_id: room.room_cycle_id,
        day: room.current_day,
        task_type: room.flush,
        notes: `Harvested ${weight} kg`
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Harvest logged successfully!');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log harvest');
    } finally {
      setSubmitting(false);
    }
  };

  const isFlush1 = room.flush === 'Flush 1';

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]" data-testid="harvest-log-modal">
        <DialogHeader>
          <DialogTitle>Log Harvest - {room.room_name}</DialogTitle>
          <div className="flex gap-2 mt-1">
            <Badge className={isFlush1 ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'}>
              {room.flush}
            </Badge>
            <span className="text-sm text-gray-500">Day {room.current_day} ({room.flush} Day {room.day_in_flush})</span>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Label htmlFor="weight">Harvest Weight (kg)</Label>
            <Input
              id="weight"
              type="number"
              step="0.1"
              data-testid="harvest-weight-input"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder="Enter weight in kg"
              required
            />
          </div>

          <div>
            <Label htmlFor="notes">Notes (Optional)</Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any observations..."
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-harvest"
              disabled={submitting || !weight}
              className="flex-1 bg-[#1A4D2E] hover:bg-[#143D23] text-white"
            >
              {submitting ? 'Logging...' : 'Log Harvest'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Production Estimate Modal
const ProductionEstimateModal = ({ room, existingEstimate, onClose, onSuccess }) => {
  const [estimate, setEstimate] = useState('');
  const [isMorning, setIsMorning] = useState(true);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const hour = new Date().getHours();
    setIsMorning(hour < 12);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/production-estimates`, {
        room_id: room.room_id,
        room_cycle_id: room.room_cycle_id,
        estimate_kg: parseFloat(estimate),
        is_morning: isMorning,
        notes
      }, { headers: { Authorization: `Bearer ${token}` } });
      
      toast.success(`Production estimate submitted for ${room.room_name}!`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit estimate');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[400px]" data-testid="production-estimate-modal">
        <DialogHeader>
          <DialogTitle>Production Estimate - {room.room_name}</DialogTitle>
          <div className="flex gap-2 mt-1">
            <Badge className={room.phase === 'flush_1' ? 'bg-yellow-100 text-yellow-800' : 'bg-orange-100 text-orange-800'}>
              {room.phase === 'flush_1' ? 'Flush 1' : 'Flush 2'}
            </Badge>
            <span className="text-sm text-gray-500">{room.batch_name}</span>
          </div>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {existingEstimate && (
            <div className="bg-gray-50 p-3 rounded-lg border">
              <p className="text-sm text-gray-600">
                <strong>Previous Estimate:</strong> {existingEstimate.estimate_kg} kg ({existingEstimate.is_morning ? 'AM' : 'PM'})
              </p>
            </div>
          )}

          <div>
            <Label>Estimate Type</Label>
            <div className="flex gap-2 mt-2">
              <Button
                type="button"
                variant={isMorning ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsMorning(true)}
                className={isMorning ? 'bg-yellow-500 hover:bg-yellow-600' : ''}
              >
                Morning
              </Button>
              <Button
                type="button"
                variant={!isMorning ? 'default' : 'outline'}
                size="sm"
                onClick={() => setIsMorning(false)}
                className={!isMorning ? 'bg-blue-500 hover:bg-blue-600' : ''}
              >
                Afternoon
              </Button>
            </div>
          </div>

          <div>
            <Label htmlFor="estimate">Estimated Production (kg)</Label>
            <Input
              id="estimate"
              type="number"
              step="0.1"
              data-testid="estimate-input"
              value={estimate}
              onChange={(e) => setEstimate(e.target.value)}
              placeholder="Enter estimated kg"
              required
            />
          </div>

          <div>
            <Label htmlFor="est_notes">Notes (Optional)</Label>
            <Textarea
              id="est_notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Any observations..."
              rows={2}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} disabled={submitting} className="flex-1">
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="submit-estimate"
              disabled={submitting || !estimate}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
            >
              {submitting ? 'Submitting...' : 'Submit Estimate'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ProductionDashboard;
